import { getAccountConfigData } from "./modules/config/AccountConfigDataGetter/getAccountConfigData";
import { setAccountConfigData } from "./modules/config/AccountConfigDataSetter/setAccountConfigData";
import { createClientWrapper as fromModuleCreateClient } from "./modules/client/ClientProvider/createClientWrapper";
import { getPublicConfigData as fromModuleGetPublicConfig } from "./modules/config/PublicConfigProvider/getPublicConfigData";
import { getFeaturesConfig as fromModuleGetFeaturesConfig } from "./modules/config/FeaturesConfigDataGetter/getFeaturesConfig";
import type { DeepPartial } from "./utils/DeepPartial";
import type { ClientOptions } from "./modules/client";
import type { XOR } from "./utils/utilityTypes";
import type { AccountSidOption, RuntimeDomainOption, AccountConfigOptions, AccountConfigData } from "./modules/config";
import type { AuthenticationConfigParams } from "./modules/auth/AuthenticationConfig";
import type { RefreshTokenParams, RefreshTokenResponse } from "./modules/auth/RefreshToken";
import type { ExchangeTokenParams, ExchangeTokenResponse } from "./modules/auth/ExchangeToken";
import { getLoginDetails as fromModuleGetLoginDetails } from "~/modules/auth/GetLoginDetails";
import { getAuthenticationConfig as fromModuleGetAuthenticationConfig } from "~/modules/auth/AuthenticationConfig";
import { exchangeTokenWrapper as fromModuleExchangeToken } from "~/modules/auth/ExchangeToken";
import { refreshTokenWrapper as fromModuleRefreshToken } from "~/modules/auth/RefreshToken";
import { validateTokenWrapper as fromModuleValidateToken } from "~/modules/auth/ValidateToken";
import packageInfo from "package.json";
import type { LoginDetailsParams } from "~/modules/auth/GetLoginDetails";


export type { SetWorkerAttributesOptions } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export { SetWorkerAttributes } from "~/modules/actions/Supervisor/SetWorkerAttributes/SetWorkerAttributes";
export type { SetWorkerActivityOptions } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";
export { SetWorkerActivity } from "~/modules/actions/Supervisor/SetWorkerActivity/SetWorkerActivity";

export type { SetAttributesOptions } from "~/modules/actions/Worker/SetAttributes/SetAttributes";
export { SetAttributes } from "~/modules/actions/Worker/SetAttributes/SetAttributes";

export type { SetCurrentActivityOptions } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";
export { SetCurrentActivity } from "~/modules/actions/Worker/SetCurrentActivity/SetCurrentActivity";

export type { SetTaskAttributesOptions } from "~/modules/actions/Task/SetTaskAttributes/SetTaskAttributes";
export { SetTaskAttributes } from "~/modules/actions/Task/SetTaskAttributes/SetTaskAttributes";

export { AcceptTask } from "~/modules/actions/Task/AcceptTask/AcceptTask";
export type { AcceptTaskOptions } from "~/modules/actions/Task/AcceptTask/AcceptTask";

export { RejectTask } from "~/modules/actions/Task/RejectTask/RejectTask";
export type { RejectTaskOptions } from "~/modules/actions/Task/RejectTask/RejectTask";

export { CompleteTask } from "~/modules/actions/Task/CompleteTask/CompleteTask";

export { EndTask } from "~/modules/actions/Task/EndTask/EndTask";

export { WrapUpTask } from "~/modules/actions/Task/WrapUpTask/WrapUpTask";

export { CancelVoiceTaskTransfer } from "~/modules/actions/Voice/CancelVoiceTaskTransfer/CancelVoiceTaskTransfer";

export type { TransferTaskOptions } from "~/modules/actions/Voice/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { StartVoiceTaskTransfer } from "~/modules/actions/Voice/StartVoiceTaskTransfer/StartVoiceTaskTransfer";

export { GetTaskParticipants } from "~/modules/actions/Task/GetTaskParticipants/GetTaskParticipants";
export type { GetTaskParticipantsResponse } from "~/modules/actions/Task/GetTaskParticipants/GetTaskParticipants";

export { AddTaskParticipantListener } from "~/modules/actions/Task/AddTaskParticipantListener/AddTaskParticipantListener";

export type {
    TaskParticipantEvent,
    AddTaskParticipantListenerResponse
} from "~/modules/actions/Task/AddTaskParticipantListener/AddTaskParticipantListener";

export type {
    ParticipantCallStatus,
    ReasonParticipantLeft,
    StatusCallbackEvent,
    VoiceMediaProperties,
    BaseParticipant,
    TaskParticipant,
    VoiceTaskParticipant,
    ConversationTaskParticipant,
    ConversationMediaProperties
} from "~/modules/participant";

export { StartOutboundCall } from "~/modules/actions/Voice/StartOutboundCall/StartOutboundCall";
export type { StartOutboundCallOptions } from "~/modules/actions/Voice/StartOutboundCall/StartOutboundCall";

export type { HoldVoiceParticipantOptions } from "~/modules/actions/Voice/HoldVoiceParticipant/HoldVoiceParticipant";
export { HoldVoiceParticipant } from "~/modules/actions/Voice/HoldVoiceParticipant/HoldVoiceParticipant";

export { UnholdVoiceParticipant } from "~/modules/actions/Voice/UnholdVoiceParticipant/UnholdVoiceParticipant";

export { KickVoiceParticipant } from "~/modules/actions/Voice/KickVoiceParticipant/KickVoiceParticipant";

export { EndVoiceCallForAll } from "~/modules/actions/Voice/EndVoiceCallForAll/EndVoiceCallForAll";

export { MonitorCall } from "~/modules/actions/Voice/MonitorCall/MonitorCall";
export type { MonitorCallOptions } from "~/modules/actions/Voice/MonitorCall/MonitorCall";

export { CoachCall } from "~/modules/actions/Voice/CoachCall/CoachCall";
export type { CoachCallOptions } from "~/modules/actions/Voice/CoachCall/CoachCall";

export { BargeCall } from "~/modules/actions/Voice/BargeCall/BargeCall";

export { AddVoiceEventListener } from "~/modules/actions/Voice/AddVoiceEventListener/AddVoiceEventListener";
export type {
    AddVoiceListenerEvent,
    AddVoiceEventListenerOptions,
    AddVoiceEventListenerResponse
} from "~/modules/actions/Voice/AddVoiceEventListener/AddVoiceEventListener";

export { AddExternalVoiceParticipant } from "~/modules/actions/Voice/AddExternalVoiceParticipant/AddExternalVoiceParticipant";
export type { AddExternalVoiceParticipantOptions } from "~/modules/actions/Voice/AddExternalVoiceParticipant/AddExternalVoiceParticipant";

export { AddConversationEventListener } from "~/modules/actions/Conversation/AddConversationEventListener/AddConversationEventListener";
export type {
    ConversationClientEvents,
    AddConversationEventListenerResponse
} from "~/modules/actions/Conversation/AddConversationEventListener/AddConversationEventListener";

export { LeaveConversation } from "~/modules/actions/Conversation/LeaveConversation/LeaveConversation";

export { PauseConversation } from "~/modules/actions/Conversation/PauseConversation/PauseConversation";

export { StartConversationTransfer } from "~/modules/actions/Conversation/StartConversationTransfer/StartConversationTransfer";
export type { StartConversationTransferOptions } from "~/modules/actions/Conversation/StartConversationTransfer/StartConversationTransfer";

export { GetConversationTransfers } from "~/modules/actions/Conversation/GetConversationTransfers/GetConversationTransfers";
export type {
    GetConversationTransfersOptions,
    ConversationTransfer
} from "~/modules/actions/Conversation/GetConversationTransfers/GetConversationTransfers";

export { AddEmailParticipant } from "~/modules/actions/Conversation/AddEmailParticipant/AddEmailParticipant";
export type { AddEmailParticipantOptions } from "~/modules/actions/Conversation/AddEmailParticipant/AddEmailParticipant";

export { RemoveEmailParticipant } from "~/modules/actions/Conversation/RemoveEmailParticipant/RemoveEmailParticipant";

export { StartOutboundEmailTask } from "~/modules/actions/Conversation/StartOutboundEmailTask/StartOutboundEmailTask";
export type {
    StartOutboundEmailTaskOptions,
    StartOutboundEmailTaskResponse
} from "~/modules/actions/Conversation/StartOutboundEmailTask/StartOutboundEmailTask";

export { GetConversationByTask } from "~/modules/actions/Conversation/GetConversationByTask/GetConversationByTask";

export { GetConversationBySid } from "~/modules/actions/Conversation/GetConversationBySid/GetConversationBySid";

export { GetConversationsUser } from "~/modules/actions/Conversation/GetConversationsUser/GetConversationsUser";

export { GetCallByTask } from "~/modules/actions/Voice/GetCallByTask/GetCallByTask";
export type { GetCallByTaskOptions } from "~/modules/actions/Voice/GetCallByTask/GetCallByTask";

export { GetPausedConversations } from "~/modules/actions/Conversation/GetPausedConversations/GetPausedConversations";
export type { GetPausedConversationsOptions } from "~/modules/actions/Conversation/GetPausedConversations/GetPausedConversations";

export type { PausedConversation } from "~/modules/actions/Conversation/GetPausedConversations/GetPausedConversations";

export { GetContentTemplates } from "~/modules/actions/Conversation/GetContentTemplates/GetContentTemplates";
export type {
    GetContentTemplatesOptions,
    ContentTemplate,
    ApprovalRequests
} from "~/modules/actions/Conversation/GetContentTemplates/GetContentTemplates";

export { ResumeConversation } from "~/modules/actions/Conversation/ResumeConversation/ResumeConversation";
export type { ResumeConversationResponse } from "~/modules/actions/Conversation/ResumeConversation/ResumeConversation";

export { GetChannelsForTask } from "~/modules/actions/Task/GetChannelsForTask/GetChannelsForTask";
export type { TaskChannel } from "~/modules/actions/Task/GetChannelsForTask/GetChannelsForTask";

export type { Action, TaskReservation } from "~/modules/actions/Action";
export type { Paginator } from "~/modules/actions/Paginator";


export type {
    AuthenticationConfigParams,
    AuthenticationConfigResponse,
    AuthenticationConfig
} from "./modules/auth/AuthenticationConfig";
export type { LegacyTokenData, SSOTokenData, TokenData, AppType, TokenRefreshResult } from "./modules/auth";
export type { RefreshTokenParams, RefreshTokenResponse } from "~/modules/auth/RefreshToken";
export type {
    ExchangeTokenParams,
    ExchangeTokenResponse,
    ConsoleExchangeTokenParams,
    SSOExchangeTokenParams
} from "~/modules/auth/ExchangeToken";
export type {
    LoginDetailsParams,
    LoginDetailsResponse,
    SSOLoginDetailsParams,
    ConsoleLoginDetailsParams
} from "./modules/auth/GetLoginDetails";


export type {
    SessionOptions,
    DefaultSessionOptions,
    ConsoleSessionOptions,
    SSOSessionOptions
} from "./modules/session";
export type {
    AccountConfigData,
    IntegrationsConfig,
    OutboundFlows,
    SkillDefinition,
    QueueStatsConfiguration,
    PublicConfig,
    PublicConfigAttributes,
    AccountSidOption,
    RuntimeDomainOption,
    TaskQueue,
    WorkerChannel,
    DefaultQueueStatsConfiguration,
    QueueConfiguration,
    QueueChannelConfiguration,
    FeaturesConfig,
    FeaturesList,
    AccountConfigOptions,
    ChannelConfig,
    CbmAttachments as Attachments,
    DebuggerIntegration,
    FlexUiStatusReport,
    CitrixVoiceVdi,
    TelemetryOptions
} from "./modules/config";
export type { Logger, LoggerOptions, LoggerLevel, LoggerLevelNumbers } from "./modules/logger";
export type { Client, ClientOptions, ClientEventsType, ConnectionState, SdkConnectionState } from "./modules/client";
export { ClientEvent, ConversationClientEvent, VoiceClientEvent } from "./modules/client";

export type { VoiceCall, HoldCallOptions } from "~/modules/VoiceCall";

export { MessagesDirection } from "~/modules/Conversation";

export { ContentTemplateVariable } from "~/modules/conversations";
export type {
    Conversation,
    SendTextMessageOptions,
    SendEmailMessageOptions,
    SendMediaMessageOptions
} from "~/modules/Conversation";
export type {
    ConversationsUser,
    UserUpdateReason,
    TwilioConversation,
    Message,
    MessageType,
    MessageUpdateReason,
    DeliveryStatus,
    Media,
    ConversationParticipant,
    ConversationsClient,
    MessageBuilder,
    MediaCategory,
    ConversationParticipantType,
    ParticipantEmailLevel,
    ConversationParticipantUpdateReason,
    ConversationStatus,
    ConversationUpdateReason,
    NotificationLevel,
    ConnectionError,
    TwilsockConnectionState
} from "~/modules/conversations";

export type { DataClientOptions, TypePolicies } from "~/modules/dataClient";
export { InitializeDataClient } from "~/modules/dataClient/InitializeDataClient";

export type { VoiceOptions } from "./modules/voice/voiceOptions";


export type { DeepPartial } from "./utils/DeepPartial";
export type { Without, XOR } from "./utils/utilityTypes";


export type { TaskRouterOptions } from "./packages/taskrouter/TaskRouterOptions";
export { Supervisor, Worker, Workspace } from "./packages/taskrouter";
export * from "./packages/taskrouter/types";


export type {
    AddVoiceParticipantResponse,
    AddVoiceParticipantOptions,
    AddVoiceParticipantMediaProperties,
    ParticipantResponse,
    Transfer,
    ParticipantCallProperties,
    ParticipantCallStatusProperties,
    ParticipantCallRecordProperties,
    ParticipantSipProperties,
    MediaProperties,
    ParticipantEventData,
    RoutingData,
    PausedChannelsResponse
} from "./modules/cbm";
export { ParticipantType, ParticipantLevel, MediaChannelType, RoutingProperties } from "~/modules/cbm";



















export const getPublicConfig = (option: XOR<AccountSidOption, RuntimeDomainOption>) =>
    fromModuleGetPublicConfig(option);

















export const getAccountConfig = (sessionToken: string, options?: AccountConfigOptions) =>
    getAccountConfigData(sessionToken, options);










export const setAccountConfig = (accountSid: string, sessionToken: string, config: Partial<AccountConfigData>) =>
    setAccountConfigData(accountSid, sessionToken, config);





















export const getAuthenticationConfig = (config: AuthenticationConfigParams) =>
    fromModuleGetAuthenticationConfig(config);


































export const getLoginDetails = async (options: LoginDetailsParams) => fromModuleGetLoginDetails(options);



















export const validateToken = (accountSid: string, token: string) => fromModuleValidateToken(accountSid, token);


















export const refreshToken = (options: RefreshTokenParams): Promise<RefreshTokenResponse> =>
    fromModuleRefreshToken(options);























export const exchangeToken = (options: ExchangeTokenParams): Promise<ExchangeTokenResponse> =>
    fromModuleExchangeToken(options);


















export const getFeaturesConfig = (token: string, uiVersion?: string) => fromModuleGetFeaturesConfig(token, uiVersion);






















export const createClient = (token: string, userOptions?: DeepPartial<ClientOptions>) =>
    fromModuleCreateClient(token, userOptions);





export const version: string = packageInfo.version;

export type { FlexSdkErrorMetadata } from "./modules/error";
export { FlexSdkError, ErrorCode, ErrorSeverity } from "./modules/error";
